import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useMutation } from '@apollo/client';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button, Col, Row } from 'react-bootstrap';
import { AlertTriangle, Key } from 'react-feather';
import { Form, Formik } from 'formik';
import { logger } from '../../utils/logging';
import { emailAtom } from '../../state/atoms';
import { SendVerifyEmailDocument } from '../../queries';
import { environment } from '../../../environments/environment';
import styles from './activate.module.scss';

export function Activate() {
  const [error, setError] = useState<string>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = useAtomValue(emailAtom);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sendVerifyEmail] = useMutation(SendVerifyEmailDocument);

  const handleSubmit = async (registration: string | null) => {
    setError(undefined);

    if (!email || !executeRecaptcha) {
      setError('Unexpected error.');
      return;
    }

    logger.info('Activate submit', { email, registration });

    let captchaToken: string;
    try {
      captchaToken = await executeRecaptcha('activate');
    } catch (err) {
      logger.error('Failed to execute recaptcha', err);
      setError('Unexpected error.');
      return;
    }

    return sendVerifyEmail({
      variables: {
        email,
        redirectUri: environment.defaultRedirectUri,
        captchaToken,
        registration,
      },
    })
      .then(({ data }) => {
        if (!data?.sendVerifyEmail) {
          setError('Unexpected error.');
          return;
        }

        navigate(`/verify-email?${searchParams.toString()}`, {
          state: { from: 'activate' },
        });
      })
      .catch((err) => {
        logger.error(err);
        setError('Unexpected error.');
      });
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={(_values, { setSubmitting }) =>
        handleSubmit(searchParams.get('registration')).finally(() =>
          setSubmitting(false)
        )
      }
    >
      {({ isSubmitting }) => (
        <Form className={styles['page']}>
          <Row>
            <Col>
              <p className={'header'}>Activate your account</p>
              <p className={'subtext'}>
                Your company and email are in our system. You still need to set
                up your password.
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className={styles['info-panel']}>
                <div className={styles['info-icon']} aria-hidden={'true'}>
                  <Key size={60} strokeWidth={2} />
                </div>
                <div className={styles['info-text']}>
                  We&apos;ll send you a link to set your password
                </div>
              </div>
            </Col>
          </Row>
          {error && (
            <Row>
              <Col>
                <div className={styles['error-panel']} role={'alert'}>
                  <div className={styles['error-icon']} aria-hidden={'true'}>
                    <AlertTriangle size={16} strokeWidth={2} />
                  </div>
                  <div className={styles['error-text']}>{error}</div>
                </div>
              </Col>
            </Row>
          )}
          <Row className={'form-button-row'}>
            <Col>
              <Button
                type={'submit'}
                disabled={isSubmitting}
                color="primary"
                style={{ width: '100%' }}
              >
                Activate now
              </Button>
            </Col>
          </Row>
          <Row className={'pt-2'}>
            <Col className={styles['sign-in-row']}>
              Already have an account?{' '}
              <a href={`/authorize`}>Sign in to SPINS account</a>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
}

export default Activate;
